// Skills v2 — library-level "Quality & Improve" tab.
//
// The mockup (docs/design/skills-framework-v2-mockup.html <section id="s7">)
// puts Quality & Improve in the Skills-library sub-nav. The surface itself is
// agent-scoped ("how this agent is doing"), so this tab is a thin host: pick an
// agent, then render the shared QualitySection — the exact same unified
// closed-loop surface shown on the agent's Overview. One implementation, two
// entry points.
import { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { agentApi } from "../../api/client";
import QualitySection from "../library/viewer/QualitySection";
import SearchableSelect from "../../components/SearchableSelect";
import { toOptions } from "../../components/comboboxLogic";

const MUTED = COLORS.textMuted || "#64748b";

function unwrapList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export default function QualityImproveTab() {
  const [agents, setAgents] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await agentApi.list();
        if (!alive) return;
        const list = unwrapList(res).filter((a) => a && !a.metadata_json?.ephemeral);
        setAgents(list);
        if (list[0]) setAgentId((prev) => prev || list[0].id);
      } catch {
        if (alive) setError("Couldn't load your agents.");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) {
    return <div style={{ fontSize: 13, color: COLORS.orange || "#d97706" }}>{error}</div>;
  }
  if (agents === null) {
    return <div style={{ fontSize: 13, color: MUTED }}>Loading agents…</div>;
  }
  if (agents.length === 0) {
    return (
      <div style={{
        border: `1px solid ${COLORS.border}`, borderRadius: 12, background: COLORS.surface,
        padding: "20px 22px", fontSize: 13, color: MUTED,
      }}>
        No agents yet. Create an agent and attach a skill — its quality &amp; improvement surface appears here.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textDim }}>Agent</span>
        <SearchableSelect
          idPrefix="qi-agent"
          placeholder="Search agents…"
          options={toOptions(agents)}
          value={agentId}
          onChange={setAgentId}
          minWidth={240}
        />
        <span style={{ fontSize: 12, color: COLORS.textDim }}>
          — the same surface lives on each agent's Overview.
        </span>
      </div>

      {agentId && <QualitySection key={agentId} agentId={agentId} />}
    </div>
  );
}
